import { observer } from 'mobx-react';
import { Component, FC, Fragment, PropsWithChildren } from 'react';
import { Divider } from 'semantic-ui-react';

import { CampaignsApi, CampaignsStore } from '@servicetitan/application-data/campaigns';
import { CultureApi, CultureStore } from '@servicetitan/application-data/culture';
import { FeaturesApi, FeaturesStore } from '@servicetitan/application-data/features';
import {
    BrandApi,
    IsDialpadAdminApi,
    IsDialpadAdminStore,
    PhonesPro,
} from '@servicetitan/application-data/telecom';
import { Permissions, UserApi, UserStore } from '@servicetitan/application-data/user';
import { Culture, CultureProvider } from '@servicetitan/culture';
import { Button, Spinner, Stack, Text } from '@servicetitan/design-system';
import { injectDependency, provide } from '@servicetitan/react-ioc';

import { DEPRECATED_RouteComponentProps, DEPRECATED_withRouter } from '../../with-router';
import { DefaultNumbersStore } from '../stores/default-numbers.store';
import { PhoneNumbersExportStore } from '../stores/phone-numbers-export.store';
import { PhoneNumbersStore } from '../stores/phone-numbers.store';
import { PlayGreetingStore } from '../stores/play-greeting.store';
import { AddEditNumber } from './add-edit-number/add-edit-number';
import { DefaultNumbers } from './default-numbers';
import { PhoneNumbersExport } from './phone-numbers-export';
import { PhoneNumbersGrid } from './phone-numbers-grid';

@provide(
    {
        singletons: [
            PhoneNumbersStore,
            PhoneNumbersExportStore,
            DefaultNumbersStore,
            PlayGreetingStore,
            PhonesPro,
            IsDialpadAdminStore,
            IsDialpadAdminApi,
            UserStore,
            UserApi,
            FeaturesStore,
            FeaturesApi,
            CampaignsStore,
            CampaignsApi,
            CultureStore,
            CultureApi,
            BrandApi,
        ],
    },
    <Spinner />
)
export class PhoneNumbersProvider extends Component<PropsWithChildren> {
    @injectDependency(PhoneNumbersStore)
    declare private phoneNumbersStore: PhoneNumbersStore;
    @injectDependency(DefaultNumbersStore)
    declare private defaultNumbersStore: DefaultNumbersStore;
    @injectDependency(PhoneNumbersExportStore)
    declare private phoneNumbersExportStore: PhoneNumbersExportStore;
    @injectDependency(CultureStore)
    declare private cultureStore: CultureStore;

    componentDidMount() {
        this.phoneNumbersStore.loadData();
        this.defaultNumbersStore.loadData();
        this.phoneNumbersExportStore.loadData();
        this.phoneNumbersExportStore.bindEventHandlers();
    }

    componentWillUnmount() {
        this.phoneNumbersExportStore.unbindEventHandlers();
    }

    render() {
        const { children } = this.props;
        const { culture } = this.cultureStore;

        return <CultureProvider value={culture as Culture}>{children}</CultureProvider>;
    }
}

@observer
class PhoneNumbersWithoutProviderUnwrapped extends Component<DEPRECATED_RouteComponentProps> {
    @injectDependency(PhoneNumbersStore)
    declare private phoneNumbersStore: PhoneNumbersStore;
    @injectDependency(PhoneNumbersExportStore)
    declare private phoneNumbersExportStore: PhoneNumbersExportStore;
    @injectDependency(UserStore)
    declare private userProfileStore: UserStore;

    render() {
        const canEdit = this.userProfileStore.hasPermission(Permissions.EditPhone);
        return (
            <Fragment>
                <Stack justifyContent="space-between" alignItems="flex-end" className="m-b-4">
                    <Text size={5} bold>
                        Phone Numbers
                    </Text>
                    <Stack spacing={2}>
                        {!!this.phoneNumbersStore.phoneNumbersGridState.totalCount && (
                            <Button primary outline onClick={this.phoneNumbersExportStore.export}>
                                Export Phone Numbers
                            </Button>
                        )}
                        {canEdit && (
                            <Fragment>
                                <AddEditNumber id={this.phoneNumbersStore.editNumberId} />
                                <Button primary onClick={this.phoneNumbersStore.openAddModal}>
                                    Add New Number
                                </Button>
                            </Fragment>
                        )}
                    </Stack>
                </Stack>
                <Divider />
                <DefaultNumbers />
                {this.phoneNumbersStore.phoneNumbersGridState.totalCount ? (
                    <Fragment>
                        <PhoneNumbersGrid canEdit={canEdit} />
                        <PhoneNumbersExport />
                    </Fragment>
                ) : (
                    <div className="ta-center m-t-5">
                        <img src={require('../assets/phone-illustration-2.svg')} />
                        <Text size={4} bold className="m-t-5">
                            You currently do not have any phone numbers.
                        </Text>
                    </div>
                )}
            </Fragment>
        );
    }
}

const PhoneNumbersWithoutProvider = DEPRECATED_withRouter(PhoneNumbersWithoutProviderUnwrapped);

export const PhoneNumbers: FC = () => (
    <PhoneNumbersProvider>
        <PhoneNumbersWithoutProvider />
    </PhoneNumbersProvider>
);
